import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useSelector } from 'react-redux';
import useVoiceTenantLines, { normalizeVoiceE164 } from './useVoiceTenantLines';
import useWhatsappCallDefaultFrom from '../../calls/useWhatsappCallDefaultFrom';
import {
  getOrFetchSipStatus,
  peekSipStatusCache
} from '../infrastructure/voiceSipStatusCache';
import { filterTenantLinesBySipStatus } from '../domain/voiceSipStatus';

const STORAGE_PREFIX = 'mercately_voice_selected_from_';

/**
 * @param {string|number|null|undefined} retailerId
 * @param {string} from
 */
function persistFrom(retailerId, from) {
  if (retailerId == null || typeof sessionStorage === 'undefined') return;
  try {
    if (from) {
      sessionStorage.setItem(`${STORAGE_PREFIX}${retailerId}`, from);
    } else {
      sessionStorage.removeItem(`${STORAGE_PREFIX}${retailerId}`);
    }
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Outbound `from` (business E.164). Tenant lines are filtered by SIP-ready status
 * (tenant ∩ BSP). One dialable line is used automatically; two or more require
 * an explicit user choice in the selector each time (never restore a persisted line).
 *
 * @param {{ enabled?: boolean }} [options]
 */
export default function useVoiceOutboundFrom({ enabled = true } = {}) {
  const retailerId = useSelector((state) => state.retailerUsersReducer?.retailer_info?.id);
  const retailerPhoneFallback = useWhatsappCallDefaultFrom();
  const {
    enabledLines: tenantEnabledLines,
    loading: tenantLoading,
    error
  } = useVoiceTenantLines({ enabled });
  const [selectedFrom, setSelectedFromState] = useState('');
  const [sipPayload, setSipPayload] = useState(() => peekSipStatusCache());
  const [sipLoading, setSipLoading] = useState(() => (
    Boolean(enabled && peekSipStatusCache() == null)
  ));
  const [sipFailed, setSipFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSipLoading(false);
      setSipFailed(false);
      return undefined;
    }

    const cached = peekSipStatusCache();
    if (cached != null) {
      setSipPayload(cached);
      setSipLoading(false);
      setSipFailed(false);
      return undefined;
    }

    let cancelled = false;
    setSipLoading(true);

    getOrFetchSipStatus()
      .then((payload) => {
        if (cancelled) return;
        setSipPayload(payload);
        setSipFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSipPayload(null);
        setSipFailed(true);
      })
      .finally(() => {
        if (!cancelled) setSipLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const enabledLines = useMemo(() => {
    if (!enabled) return [];
    if (sipLoading || sipFailed) return [];
    return filterTenantLinesBySipStatus(tenantEnabledLines, sipPayload);
  }, [enabled, sipFailed, sipLoading, sipPayload, tenantEnabledLines]);

  const loading = tenantLoading || (enabled && sipLoading);
  const noSipReadyLines = Boolean(
    enabled
    && !loading
    && !sipFailed
    && tenantEnabledLines.length > 0
    && enabledLines.length === 0
  );

  useEffect(() => {
    if (!enabled) {
      setSelectedFromState('');
      return;
    }

    if (loading) return;

    if (enabledLines.length === 1) {
      setSelectedFromState(enabledLines[0].businessE164);
      persistFrom(retailerId, enabledLines[0].businessE164);
      return;
    }

    if (enabledLines.length > 1) {
      // Multi Voice: never auto-pick / restore a line — user must choose in the selector.
      // Keep the in-session selection only while it remains a SIP-ready option.
      setSelectedFromState((current) => {
        const stillValid = enabledLines.some((line) => line.businessE164 === current);
        return stillValid ? current : '';
      });
      return;
    }

    // No dialable SIP lines: clear selection (do not fall back to a non-SIP from).
    if (tenantEnabledLines.length > 0 || sipFailed) {
      setSelectedFromState('');
      return;
    }

    setSelectedFromState(retailerPhoneFallback);
  }, [
    enabled,
    enabledLines,
    loading,
    retailerId,
    retailerPhoneFallback,
    sipFailed,
    tenantEnabledLines.length
  ]);

  const setSelectedFrom = useCallback((from) => {
    const normalized = normalizeVoiceE164(from);
    setSelectedFromState(normalized);
    persistFrom(retailerId, normalized);
  }, [retailerId]);

  const requiresFromSelection = enabled && enabledLines.length > 1 && !selectedFrom;
  const mustConfirmFrom = enabled && enabledLines.length > 1;
  const canPlaceOutboundCall = Boolean(selectedFrom) && !requiresFromSelection && !loading
    && !noSipReadyLines && !sipFailed;

  const selectedLine = useMemo(() => {
    if (!selectedFrom) return null;
    return enabledLines.find((line) => line.businessE164 === selectedFrom) || null;
  }, [enabledLines, selectedFrom]);

  // Single-number (legacy, no multi-number): the line scope (appId / platformRetailerId)
  // must NOT be sent. The backend resolves the legacy line from the flag `multi_number?`
  // and the outbound `from` number. Only multi-number (>1 SIP-ready line, requires an
  // explicit selection) scopes the permission check and createCall to a specific line.
  const selectedAppId = mustConfirmFrom ? (selectedLine?.appId ?? null) : null;
  const selectedPlatformRetailerId = mustConfirmFrom
    ? (selectedLine?.platformRetailerId ?? null)
    : null;

  return useMemo(() => ({
    enabledLines,
    selectedFrom,
    selectedLine,
    selectedAppId,
    selectedPlatformRetailerId,
    setSelectedFrom,
    requiresFromSelection,
    mustConfirmFrom,
    canPlaceOutboundCall,
    loading,
    error,
    noSipReadyLines,
    sipFailed
  }), [
    enabledLines,
    selectedFrom,
    selectedLine,
    selectedAppId,
    selectedPlatformRetailerId,
    setSelectedFrom,
    requiresFromSelection,
    mustConfirmFrom,
    canPlaceOutboundCall,
    loading,
    error,
    noSipReadyLines,
    sipFailed
  ]);
}
